import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Box,
  BoxProps,
  Button,
  Collapse,
  Container,
  Fade,
  Grid,
  Stack,
  SxProps,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import {
  ISubmissionQuestion,
  createSubmissionAnswer,
  endSubmissionQuestionTimer,
  finishSubmission,
  getSubmissionById,
  getSubmissionQuestion,
  getSubmissionState,
  resetSubmission,
  showAiHint,
  startSubmissionQuestionTimer,
  updateSubmissionCountdownSettings,
} from '../../../store/SubmissionSlice';
import { getAuthState } from '../../../store/AuthSlice';
import {
  createDiagnostic,
  getDiagnosticBySubmissionId,
  getDiagnosticState,
} from '../../../store/DiagnosticSlice';
import {
  getAccessLimitState,
  toggleLeadGenModal,
  togglePremiumModal,
} from '../../../store/AccessLimitSlice';
import {
  ISubTopic,
  getSubTopicById,
  getSubTopicState,
} from '../../../store/SubTopicSlice';
import { ITopic } from '../../../store/TopicSlice';
import { IQuestionDetailAnswer } from '../../../store/QuestionSlice';
import { saveStudentProgress } from '../../../store/ProgressSlice';
import { saveUserUsage } from '../../../store/AccessLimitSlice/AccessLimitApi';
import { getAuthUser } from '../../../store/AuthSlice/V2/AuthApi';
import { TP_COLORS } from '../../../constants';
import { useAppSelector } from '../../../hooks';
import { getCountry, getUrlConvertedString } from '../../../helper';
import Breadcrumbs, {
  BreadcrumbOption,
  getBreadCrumbOptionsByTopicAndSubTopicData,
} from '../../../components/BreadCrumb/Breadcrumbs';
import QuestionListContainer from './components/QuestionListContainer';
import QuestionDetailViewer from './components/QuestionDetailViewer';
import AnswerListItem from './components/AnswerListItem';
import LeadDetailsModal, { IDiagnosticState } from './modals/LeadDetailsModal';
import QuizSubmitConfirmationModal from './modals/QuizSubmitConfirmationModal';
import MarkingSchemeViewer from './components/MarkingSchemeViewer';
import OpenEndedQuestionAnswerHandler from './components/OpenEndedQuestionAnswerHandler';
import SubmissionTimer from './components/Timer/SubmissionTimer';
// import ContinueWithTimerModal from './modals/ContinueWithTimerModal';
import GradedSection from './components/GradedSection';
import BasicLoader from '../../../components/Loaders/BasicLoader';
import ShowAiHintModal from './modals/ShowAiHintModal';

const outerBoxProps: BoxProps['sx'] = {
  display: 'inline-block',
  alignItems: 'center',
  borderRadius: '20px',
  overflow: 'hidden',
  position: 'relative',
  padding: '2px',
  background: 'linear-gradient(90deg, #DB86F8 0%, #006FD6 100%)',
};

const animationStyles: SxProps = {
  position: 'absolute',
  left: '32%',
  top: '22%',
  transform: 'translateY(-50%)',
  width: '24px',
  height: '24px',
  border: '2px solid #01A79F',
  borderRadius: '50%',
  zIndex: 1,
  animation: 'wave-animation 1.5s infinite',
  '@keyframes wave-animation': {
    '0%': {
      transform: 'scale(1)',
      opacity: 0.8,
    },
    '25%': {
      transform: 'scale(1.2)',
      opacity: 0.7,
    },
    '50%': {
      transform: 'scale(1.3)',
      opacity: 0.5,
    },
    '75%': {
      transform: 'scale(1.4)',
      opacity: 0.4,
    },
    '100%': {
      transform: 'scale(1.5)',
      opacity: 0.3,
    },
  },
};

export type TShuffledData = {
  openAiCorrectAnswers: Array<IQuestionDetailAnswer>;
  selectedOpenAiCorrectAnswer?: IQuestionDetailAnswer;
  answers: Array<IQuestionDetailAnswer>;
};

const constructBreadcrumbOptions = (
  topicData?: ITopic,
  subTopicData?: ISubTopic
) => {
  const options: Array<BreadcrumbOption> = getBreadCrumbOptionsByTopicAndSubTopicData(
    topicData,
    subTopicData
  );

  options.push({
    type: 'text',
    title: 'Quiz',
    highlight: true,
  });

  return options;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const permutator = (answers: IQuestionDetailAnswer[]): any[] => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: any = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const permute = (options, item: any = []) => {
    if (options.length === 0) {
      result.push(item);
    } else {
      for (let i = 0; i < options.length; i += 1) {
        const current = options.slice();
        const next = current.splice(i, 1);
        permute(current.slice(), item.concat(next));
      }
    }
  };

  permute(answers);

  return result;
};

export const factorial = (n: number): number => {
  const permutationsNumber: number[] = [];

  if (n === 0 || n === 1) {
    return 1;
  }

  if (permutationsNumber[n] > 0) {
    return permutationsNumber[n];
  }

  permutationsNumber[n] = factorial(n - 1) * n;

  return permutationsNumber[n];
};

const TimeUpAlert = () => {
  return (
    <div style={{ marginBottom: '15px' }}>
      <Alert
        variant="filled"
        severity="warning"
        color="warning"
        icon={false}
        sx={{
          color: 'text.primary',
          fontWeight: 600,
          justifyContent: 'center',
        }}
      >
        Time up! You may continue to practice but your score will not change.
      </Alert>
    </div>
  );
};

const containerStyles: SxProps = {
  background: 'linear-gradient(180deg, #01736D 0%, #001615 100%)',
  minHeight: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
};

const SubmissionQuestionsManagement: React.FC = () => {
  const params = useParams();
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const belowMd = useMediaQuery(theme.breakpoints.down('md'));

  // Store
  const submissionState = useAppSelector(getSubmissionState);
  const authState = useAppSelector(getAuthState);
  const diagnosticState = useAppSelector(getDiagnosticState);
  const accessLimitState = useAppSelector(getAccessLimitState);
  const subTopicState = useAppSelector(getSubTopicState);

  const authUser = authState.user;
  const submissionData = submissionState.submission;
  const questionData = submissionState.question;
  const quizSubTopics: string[] = [];
  const subTopicData = subTopicState.subTopic;
  const quizData =
    submissionData && submissionData.quiz ? submissionData.quiz : undefined;

  const topicData = quizData && quizData.topic ? quizData.topic : undefined;
  const breadcrumbOptions = constructBreadcrumbOptions(topicData, subTopicData);
  const submissionQuestions =
    submissionData && submissionData.questions ? submissionData.questions : [];

  const pendingSubmissionQuestionCount = submissionQuestions.filter(
    (item) => item.status === 'pending'
  ).length;

  const urlConvertedCurriculum = getUrlConvertedString(
    topicData?.curriculum || ''
  );

  const urlConvertedSubject = getUrlConvertedString(topicData?.subject || '');

  const isPremiumModalToggleable =
    authUser &&
    (authUser.role.name === 'Student' || authUser.isLeadGenSubmitted);

  const isDiagnostic =
    (topicData && topicData.name === 'Diagnostic Challenge') || false;

  if (quizData && quizData.subTopics && quizData.subTopics.length) {
    quizData.subTopics.forEach((item) => {
      if (item.subTopic && item.subTopic.name) {
        quizSubTopics.push(item.subTopic.name);
      }
    });
  }

  // State
  const [isTimeUpAlertVisible, setTimeUpAlertVisible] = useState(false);
  const [isQuizCompleteConfirmOpen, setQuizCompleteConfirm] = useState(false);
  const [isLeadModalOpen, setLeadModalOpen] = useState(false);
  const [isShowAiHintModalOpen, setShowAiHintModal] = useState(false);
  const [isLeadGenSubmitted, setLeadGenSubmitted] = useState(false);
  const [showPulse, setShowPulse] = useState(false);

  const [selectedQuestion, setSelectedQuestion] = useState<
    ISubmissionQuestion | undefined
  >(undefined);

  const [
    isDiagnosticAutoRedirectReady,
    setDiagnosticAutoRedirectReady,
  ] = useState(false);

  // const [
  //   isTimerSettingsModalSubmitted,
  //   setTimeSettingsModalSubmitted,
  // ] = useState(false);

  // const [
  //   isContinueWithTimerModalOpen,
  //   setContinueWithTimerModalOpen,
  // ] = useState(false);

  useEffect(() => {
    if (params.submissionId) {
      dispatch(getSubmissionById(params.submissionId));
    }

    if (params.subtopicId) {
      dispatch(getSubTopicById(params.subtopicId));
    }

    return () => {
      dispatch(resetSubmission());

      // END QUESTION TIMER
      if (
        submissionData &&
        submissionData.status === 'pending' &&
        params.submissionId &&
        selectedQuestion &&
        selectedQuestion.question &&
        selectedQuestion.status !== 'skipped'
      ) {
        dispatch(
          endSubmissionQuestionTimer({
            submissionId: params.submissionId,
            questionId: selectedQuestion.question,
          })
        );
      }
    };
  }, [params.submissionId]);

  useEffect(() => {
    if (params.submissionId && submissionData && submissionQuestions.length) {
      if (submissionData.status === 'completed' && selectedQuestion) {
        dispatch(
          getSubmissionQuestion({
            submissionId: params.submissionId,
            questionId: selectedQuestion.question,
          })
        );
      }

      if (!selectedQuestion) {
        const firstQuestion = submissionQuestions[0];

        setSelectedQuestion(firstQuestion);

        if (firstQuestion && firstQuestion.question) {
          dispatch(
            getSubmissionQuestion({
              submissionId: params.submissionId,
              questionId: firstQuestion.question,
            })
          );
        }
      }

      if (selectedQuestion) {
        const index = submissionQuestions.findIndex(
          (questionItem) => questionItem.question === selectedQuestion.question
        );

        if (index > -1) {
          setSelectedQuestion(submissionQuestions[index]);
        }
      }

      // if (
      //   authUser &&
      //   submissionData &&
      //   submissionData.status === 'pending' &&
      //   quizData?.assessment !== true &&
      //   authUser.isContinueSubmissionWithTimerEnabled === undefined &&
      //   !isTimerSettingsModalSubmitted
      // ) {
      //   setContinueWithTimerModalOpen(true);
      // }

      if (
        authState.user &&
        authState.user.isContinueSubmissionWithTimerEnabled === true &&
        submissionData &&
        submissionData.status === 'pending' &&
        submissionData.isTimerEnabled === undefined
      ) {
        dispatch(
          updateSubmissionCountdownSettings({
            rememberOption: false,
            submissionId: submissionData._id,
            isContinueSubmissionWithTimerEnabled: true,
          })
        );
      }

      if (
        isDiagnostic &&
        submissionData.status === 'completed' &&
        diagnosticState.status !== 'loading' &&
        diagnosticState.diagnostic === undefined
      ) {
        dispatch(getDiagnosticBySubmissionId(params.submissionId));
      }
    }
  }, [params.submissionId, submissionData]);

  useEffect(() => {
    if (
      // !isContinueWithTimerModalOpen &&
      params.submissionId &&
      submissionData &&
      submissionData.status === 'pending' &&
      selectedQuestion &&
      selectedQuestion.status === 'pending' &&
      !selectedQuestion.timerStart
    ) {
      dispatch(
        startSubmissionQuestionTimer({
          submissionId: params.submissionId,
          questionId: selectedQuestion.question,
        })
      );
    }
  }, [selectedQuestion, submissionData?.timerStart]);

  useEffect(() => {
    if (
      isDiagnosticAutoRedirectReady &&
      isDiagnostic &&
      diagnosticState.diagnostic &&
      diagnosticState.diagnostic._id &&
      topicData &&
      subTopicData
    ) {
      setDiagnosticAutoRedirectReady(false);
      const urlConvertedTopic = getUrlConvertedString(topicData.name);
      const urlConvertedSubTopic = getUrlConvertedString(subTopicData.name);

      navigate(
        `${process.env.PUBLIC_URL}/dashboard/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlConvertedTopic}/${urlConvertedSubTopic}/${subTopicData._id}/diagnostic-quiz/${diagnosticState.diagnostic._id}`
      );
    }
  }, [diagnosticState.diagnostic]);

  useEffect(() => {
    if (
      accessLimitState &&
      accessLimitState.isLeadGenSuccess &&
      !isLeadGenSubmitted
    ) {
      toggleQuizCompleteConfirmation();
      setLeadGenSubmitted(true);
    }
  }, [accessLimitState]);

  useEffect(() => {
    if (
      diagnosticState.isDiagnosticSubmitted &&
      isLeadModalOpen &&
      submissionData
    ) {
      setLeadModalOpen(false);
      handleQuizComplete();
      dispatch(getAuthUser());
    }
  }, [diagnosticState]);

  const getShuffledData = () => {
    let shuffledAnswers: Array<IQuestionDetailAnswer> = [];
    let openAiCorrectAnswers: Array<IQuestionDetailAnswer> = [];
    let selectedOpenAiCorrectAnswer: IQuestionDetailAnswer | undefined;

    if (questionData) {
      shuffledAnswers = questionData.answers || [];

      if (submissionData && submissionData.shuffleFactor) {
        shuffledAnswers = permutator(questionData.answers)[
          submissionData.shuffleFactor % factorial(questionData.answers.length)
        ];

        shuffledAnswers = shuffledAnswers.map((item, index) => ({
          ...item,
          order: index,
        }));
      }

      if (shuffledAnswers.length) {
        openAiCorrectAnswers = shuffledAnswers.filter(
          (shuffledQuestion) =>
            shuffledQuestion.isCorrect && shuffledQuestion.openaiResponse
        );

        selectedOpenAiCorrectAnswer = shuffledAnswers.find(
          (shuffledQuestion) =>
            selectedQuestion &&
            selectedQuestion.answer &&
            shuffledQuestion._id === selectedQuestion.answer._id
        );
      }
    }

    return {
      openAiCorrectAnswers,
      selectedOpenAiCorrectAnswer,
      answers: shuffledAnswers,
    };
  };

  const onQuestionAnswerOptionClicked = (answer: IQuestionDetailAnswer) => {
    if (
      submissionData &&
      selectedQuestion &&
      submissionData.status === 'pending' &&
      selectedQuestion.status !== 'skipped' &&
      answer &&
      answer._id &&
      params.submissionId &&
      questionData
    ) {
      dispatch(
        createSubmissionAnswer({
          submissionId: params.submissionId,
          questionId: questionData._id,
          answerId: answer._id,
        })
      );

      // END QUESTION TIMER
      dispatch(
        endSubmissionQuestionTimer({
          submissionId: params.submissionId,
          questionId: selectedQuestion.question,
        })
      );
    }
  };

  const onQuestionBackNavigation = () => {
    if (selectedQuestion) {
      const prevQuestion = submissionQuestions[selectedQuestion.order - 1];

      if (params.submissionId && prevQuestion) {
        dispatch(
          getSubmissionQuestion({
            submissionId: params.submissionId,
            questionId: prevQuestion.question,
          })
        );
      }

      // END QUESTION TIMER
      if (
        submissionData &&
        submissionData.status === 'pending' &&
        params.submissionId &&
        selectedQuestion.question &&
        selectedQuestion.status !== 'skipped'
      ) {
        dispatch(
          endSubmissionQuestionTimer({
            submissionId: params.submissionId,
            questionId: selectedQuestion.question,
          })
        );
      }

      setSelectedQuestion(prevQuestion);
    }
  };

  const onQuestionNextNavigation = () => {
    if (selectedQuestion) {
      const nextQuestion = submissionQuestions[selectedQuestion.order + 1];

      if (params.submissionId && nextQuestion) {
        dispatch(
          getSubmissionQuestion({
            submissionId: params.submissionId,
            questionId: nextQuestion.question,
          })
        );
      }

      // END QUESTION TIMER
      if (
        submissionData &&
        submissionData.status === 'pending' &&
        params.submissionId &&
        selectedQuestion.question &&
        selectedQuestion.status !== 'skipped'
      ) {
        dispatch(
          endSubmissionQuestionTimer({
            submissionId: params.submissionId,
            questionId: selectedQuestion.question,
          })
        );
      }

      setSelectedQuestion(nextQuestion);
    }
  };

  const handleQuestionChange = (question: ISubmissionQuestion) => {
    if (params.submissionId) {
      dispatch(
        getSubmissionQuestion({
          submissionId: params.submissionId,
          questionId: question.question,
        })
      );

      // END QUESTION TIMER
      if (
        submissionData &&
        submissionData.status === 'pending' &&
        params.submissionId &&
        selectedQuestion &&
        selectedQuestion.question &&
        selectedQuestion.status !== 'skipped'
      ) {
        dispatch(
          endSubmissionQuestionTimer({
            submissionId: params.submissionId,
            questionId: selectedQuestion.question,
          })
        );
      }

      setSelectedQuestion(question);

      window.scrollTo({ top: 0 });
    }
  };

  const handleShowAiHintModalOpen = () => {
    if (questionData && params.submissionId) {
      dispatch(
        showAiHint({
          questionId: questionData._id,
        })
      );
    }

    toggleShowAiHintModal();
  };

  const toggleShowAiHintModal = () => {
    setShowAiHintModal((prevState) => !prevState);
  };

  const toggleLeadsDetailModal = () => {
    setLeadModalOpen((prevState) => !prevState);
  };

  const toggleQuizCompleteConfirmation = () => {
    setQuizCompleteConfirm((prevState) => !prevState);
  };

  const handleSubmitLeadGen = (values: IDiagnosticState) => {
    if (
      values &&
      topicData &&
      params.submissionId &&
      params.subtopicId &&
      authUser
    ) {
      dispatch(
        createDiagnostic({
          ...values,
          user: authUser._id,
          submission: params.submissionId,
          curriculum: topicData.curriculum,
          subject: topicData.subject,
          subTopicId: params.subtopicId,
        })
      );
    }
  };

  const handleShowAiHint = () => {
    if (selectedQuestion) {
      handleShowAiHintModalOpen();
    }
    setShowPulse(false);
  };

  const handleCreateDiagnostic = () => {
    if (topicData && params.submissionId && params.subtopicId && authUser) {
      dispatch(
        createDiagnostic({
          user: authUser._id,
          submission: params.submissionId,
          curriculum: topicData.curriculum,
          subject: topicData.subject,
          grade: topicData.grade,
          subTopicId: params.subtopicId,
          country: getCountry(),
        })
      );
    }

    handleQuizComplete();
  };

  const onQuizSubmit = () => {
    if (submissionData && submissionData.status === 'pending') {
      if (
        isDiagnostic &&
        authUser &&
        authUser.role &&
        authUser.role.name === 'Guest' &&
        !authUser.isLeadGenSubmitted
      ) {
        toggleLeadsDetailModal();
      } else if (
        !isDiagnostic &&
        accessLimitState.limitation &&
        accessLimitState.usage &&
        accessLimitState.limitation.quizAttempts &&
        accessLimitState.limitation.quizAttempts > 0 &&
        accessLimitState.usage.quizAttempts + 1 >=
          accessLimitState.limitation.quizAttempts
      ) {
        dispatch(
          isPremiumModalToggleable ? togglePremiumModal() : toggleLeadGenModal()
        );
      } else {
        toggleQuizCompleteConfirmation();
      }
    }
  };

  const handleQuizConfirmationSubmit = () => {
    setDiagnosticAutoRedirectReady(true);
    handleCreateDiagnostic();
  };

  const handleQuizComplete = () => {
    if (params.submissionId) {
      dispatch(
        finishSubmission({
          submissionId: params.submissionId,
        })
      );
    }

    // END QUESTION TIMER
    if (
      submissionData &&
      selectedQuestion &&
      submissionData.status === 'pending' &&
      params.submissionId &&
      selectedQuestion.question &&
      selectedQuestion.status !== 'skipped'
    ) {
      dispatch(
        endSubmissionQuestionTimer({
          submissionId: params.submissionId,
          questionId: selectedQuestion.question,
        })
      );
    }

    if (authUser && authUser.role.name === 'Student' && params.subtopicId) {
      dispatch(
        saveStudentProgress({
          subTopicId: params.subtopicId,
          quizCompletion: true,
        })
      );
    }

    if (
      authUser &&
      (authUser.role.name === 'Guest' || authUser.role.name === 'Student')
    ) {
      dispatch(
        saveUserUsage({
          userId: authUser._id,
          option: 'quizAttempts',
        })
      );
    }

    setQuizCompleteConfirm(false);
  };

  const shuffledData: TShuffledData = getShuffledData();
  const isQuestionBackNavigationEnabled =
    selectedQuestion && selectedQuestion.order > 0;

  const isQuestionNextNavigationEnabled =
    selectedQuestion && selectedQuestion.order < submissionQuestions.length - 1;

  const isOpenEndedQuestionAlertOpen = Boolean(
    questionData &&
      selectedQuestion &&
      selectedQuestion.isOpenEnded &&
      questionData.openEnded &&
      questionData.openEnded.question &&
      submissionData?.status === 'pending'
  );

  const OpenEndedQuestionAlert = () => {
    const isOpenAiGraded =
      authUser?.role.name === 'Guest' || authUser?.role.name === 'Student';
    return (
      <div style={{ marginBottom: '15px' }}>
        <Alert
          variant="filled"
          severity="info"
          icon={false}
          sx={{
            backgroundColor: '#F1FBFA',
            color: TP_COLORS.black,
            padding: '2px 12px',
            marginBottom: '15px',
            fontWeight: 600,
            fontSize: '0.8125rem',
            justifyContent: 'center',
          }}
        >
          {isOpenAiGraded
            ? 'Your AI tutor will assess theory-type questions upon submission.'
            : 'Your tutor will assess theory-type questions upon submission, and final scores will be made available at a later time.'}
        </Alert>
      </div>
    );
  };

  return (
    <Box sx={containerStyles}>
      <Container maxWidth="lg">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          style={{ padding: '30px 0' }}
        >
          <Breadcrumbs options={breadcrumbOptions} showDropDown={belowMd} />
          {submissionData &&
          authUser &&
          submissionData.timerStart &&
          submissionData.isTimerEnabled &&
          !(isDiagnostic && submissionData.status === 'completed') ? (
            <SubmissionTimer
              submissionData={submissionData}
              isTimeUpAlertVisible={isTimeUpAlertVisible}
              showTimeUpAlert={
                submissionData.status === 'pending'
                  ? () => {
                      setTimeUpAlertVisible(true);
                    }
                  : undefined
              }
            />
          ) : null}
        </Stack>
        {/* ALERTS */}
        <Collapse in={isTimeUpAlertVisible}>
          <TimeUpAlert />
        </Collapse>
        <Collapse in={isOpenEndedQuestionAlertOpen}>
          <OpenEndedQuestionAlert />
        </Collapse>
        {/* MAIN CONTENT CONTAINER */}
        <div style={{ marginBottom: '30px' }}>
          {submissionData &&
          questionData &&
          selectedQuestion &&
          selectedQuestion.question === questionData._id ? (
            <Fade in timeout={600}>
              <Grid container spacing={5}>
                {/* QUESTION DETAIL VIEWER */}
                <Grid item xs={12} lg={6}>
                  <QuestionDetailViewer
                    selectedQuestion={selectedQuestion}
                    questionData={questionData}
                    submissionData={submissionData}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  {/* ANSWER HANDLER FOR OPEN ENDED QUESTIONS */}
                  <div style={{ marginBottom: '15px' }}>
                    {selectedQuestion.isOpenEnded &&
                    questionData.openEnded &&
                    questionData.openEnded.question ? (
                      <Box>
                        <OpenEndedQuestionAnswerHandler
                          selectedQuestion={selectedQuestion}
                          submissionData={submissionData}
                        />
                      </Box>
                    ) : (
                      // ANSWER HANDLER FOR REGULAR QUESTIONS
                      <Box>
                        <Grid container spacing={2}>
                          {(shuffledData.answers || []).map((item, index) => {
                            return (
                              <Grid item xs={12} key={`an-li-${index}`}>
                                <AnswerListItem
                                  answer={item}
                                  answers={shuffledData.answers}
                                  submission={submissionData}
                                  selectedQuestion={selectedQuestion}
                                  onClick={() =>
                                    onQuestionAnswerOptionClicked(item)
                                  }
                                  isDisabled={
                                    selectedQuestion.status === 'skipped' ||
                                    submissionData.status === 'completed'
                                  }
                                />
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Box>
                    )}
                  </div>
                  {/* QUESTION NAVIGATION CONTAINER */}
                  <div style={{ marginBottom: '15px' }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      {submissionData.status === 'pending' ? (
                        <Box sx={outerBoxProps}>
                          {showPulse && <Box sx={animationStyles} />}
                          <Tooltip
                            title={
                              <div>
                                <strong>Stuck on this question?</strong> <br />
                                <br />
                                No worries! Click the AI Hint button to get a
                                helpful clue.
                              </div>
                            }
                            arrow
                            placement="bottom-end"
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  backgroundColor: '#FFF5CC',
                                  border: '1px solid #FFAB00',
                                  color: 'black',
                                  fontSize: '12px',
                                  padding: '15px',
                                  maxWidth: '240px',
                                  borderRadius: '15px',
                                  marginRight: '-100px',
                                },
                              },
                              arrow: {
                                sx: {
                                  color: '#FFF5CC',
                                  ml: '-70px',
                                },
                              },
                            }}
                          >
                            <Box
                              onMouseEnter={() => setShowPulse(true)}
                              onMouseLeave={() => setShowPulse(false)}
                            >
                              <Button
                                variant="contained"
                                color="inherit"
                                onClick={handleShowAiHint}
                                sx={{
                                  backgroundColor: TP_COLORS.white,
                                  color: '#003768',
                                  borderRadius: '30px',
                                  fontWeight: 500,
                                }}
                                endIcon={
                                  <img
                                    src={`${process.env.PUBLIC_URL}/images/backgrounds/hint.png`}
                                    alt="Hint Icon"
                                    style={{ width: '24px', height: '24px' }}
                                  />
                                }
                              >
                                Need Hint? Ask AI
                              </Button>
                            </Box>
                          </Tooltip>
                        </Box>
                      ) : (
                        <div />
                      )}

                      <Stack direction="row" alignItems="center">
                        <Button
                          variant="contained"
                          color="inherit"
                          startIcon={<ChevronLeftIcon />}
                          onClick={onQuestionBackNavigation}
                          disabled={
                            !isQuestionBackNavigationEnabled ||
                            submissionState.status === 'loading'
                          }
                          style={{
                            backgroundColor: isQuestionBackNavigationEnabled
                              ? 'rgba(248, 250, 252, 0.3)'
                              : 'rgba(248, 250, 252, 0.1)',
                            color: TP_COLORS.white,
                            borderRadius: '30px',
                            fontWeight: 500,
                            marginRight: '10px',
                          }}
                        >
                          Back
                        </Button>
                        <Button
                          variant="contained"
                          color="inherit"
                          endIcon={<ChevronRightIcon />}
                          onClick={onQuestionNextNavigation}
                          disabled={
                            !isQuestionNextNavigationEnabled ||
                            submissionState.status === 'loading'
                          }
                          style={{
                            backgroundColor: isQuestionNextNavigationEnabled
                              ? 'rgba(248, 250, 252, 0.3)'
                              : 'rgba(248, 250, 252, 0.1)',
                            color: TP_COLORS.white,
                            borderRadius: '30px',
                            fontWeight: 500,
                          }}
                        >
                          Next
                        </Button>
                      </Stack>
                    </Stack>
                  </div>
                </Grid>
              </Grid>
            </Fade>
          ) : (
            <BasicLoader loaderProps={{ style: { color: TP_COLORS.white } }} />
          )}
        </div>
        {/* OPEN ENDED GRADE DATA VIEWER */}
        {selectedQuestion &&
        questionData &&
        selectedQuestion.isOpenEnded &&
        questionData.openEnded &&
        questionData.openEnded.question &&
        selectedQuestion.question === questionData._id &&
        submissionData &&
        submissionData.status === 'completed' ? (
          <GradedSection
            selectedQuestion={selectedQuestion}
            questionData={questionData}
          />
        ) : null}
        {/* MARKING SCHEME VIEWER */}
        {submissionData &&
        submissionData.status === 'completed' &&
        questionData &&
        selectedQuestion &&
        selectedQuestion.question === questionData._id ? (
          <Fade in>
            <div style={{ marginBottom: '15px' }}>
              <MarkingSchemeViewer
                questionData={questionData}
                shuffledData={shuffledData}
                selectedQuestion={selectedQuestion}
                submissionData={submissionData}
              />
            </div>
          </Fade>
        ) : null}
      </Container>
      {/* BOTTOM QUESTION LIST CONTAINER */}
      <div style={{ minHeight: '120px' }} />
      {submissionData && subTopicData ? (
        <QuestionListContainer
          isDiagnostic={isDiagnostic}
          submission={submissionData}
          selectedQuestion={selectedQuestion}
          submitQuiz={onQuizSubmit}
          onQuestionClicked={handleQuestionChange}
        />
      ) : null}
      {/* MODALS */}
      {questionData && isShowAiHintModalOpen ? (
        <ShowAiHintModal
          open={isShowAiHintModalOpen}
          question={questionData}
          onClose={toggleShowAiHintModal}
        />
      ) : null}
      {/* {isShowAnswerConfirmationModalOpen ? (
        <ShowAnswerConfirmationModal
          open={isShowAnswerConfirmationModalOpen}
          onClose={toggleShowAnswerConfirmationModal}
          onShowAnswer={handleShowQuestionAnswer}
        />
      ) : null} */}
      {isLeadModalOpen ? (
        <LeadDetailsModal
          open={isLeadModalOpen}
          onClose={toggleLeadsDetailModal}
          onConfirm={handleSubmitLeadGen}
        />
      ) : null}
      {isQuizCompleteConfirmOpen ? (
        <QuizSubmitConfirmationModal
          open={isQuizCompleteConfirmOpen}
          onClose={toggleQuizCompleteConfirmation}
          pendingSubmissionQuestionCount={pendingSubmissionQuestionCount}
          onSubmit={() =>
            isDiagnostic ? handleQuizConfirmationSubmit() : handleQuizComplete()
          }
        />
      ) : null}
      {/* {isContinueWithTimerModalOpen ? (
        <ContinueWithTimerModal
          open={isContinueWithTimerModalOpen}
          onClose={() => setContinueWithTimerModalOpen(false)}
          onConfirm={() => setTimeSettingsModalSubmitted(true)}
        />
      ) : null} */}
    </Box>
  );
};

export default SubmissionQuestionsManagement;
